import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { ButtonIcon, MaskedLink } from '@hz-design-system/web-ui';
import { Trans } from '@ecg-marktplaats/js-react-i18n';

import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { appendHashParam, deleteHashParam, getHashParamValue } from '../../../../utils/hashParams';
import registerPhoneClick from '../../repositories/registerPhoneClick';

import LoginDialogContainer from '../../../../components/LoginDialog/LoginDialogContainer';
import Asq from './Asq';
import SellerContactOptionsButton from './SellerContactOptionsButton';

import { TListing, TListingClient, TListingSellerInfo } from 'types/TListing';

import Classes from './SellerContactOptions.scss';
import { GA_PARAMETER, GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';
import createCustomDimension from '../../../../utils/createCustomDimension';
import PaymentProposalButton from './PaymentProposal/PaymentProposalButton';
import BuyItNowButton from './BuyItNow/BuyItNowButton';

import { TSellerProfileClient } from 'types/TSellerProfile';
import { CONTACT_OPTION_TYPES } from '../../../../../../common/constants';

let PaymentProposalDialog;
let PhoneDialog;

type TSellerContactOptionsProps = {
  itemId: TListingClient['itemId'];
  title: TListingClient['title'];
  adImageThumbnail: string;
  priceInfo: TListingClient['priceInfo'];
  seller: TListingSellerInfo;
  isAutomotiveAd: TListingClient['isAutomotiveAd'];
  allowTradeInRequest: TListingClient['flags']['allowTradeInRequest'];
  isAdmarkt: TListingClient['flags']['isAdmarkt'];
  hasCallTracking: TListing['flags']['hasCallTracking'];
  traits: TListingClient['traits'];
  lowBidDetection?: TListingClient['lowBidDetection'];
  profile: TSellerProfileClient;
  isCarAd?: boolean;
};
type GaEventProps = {
  label: string;
};

const HASH_PARAMS = {
  SHOW_PHONE: {
    KEY: 'action',
    VALUE: 'showPhone',
  },
};

type SellersContactWebsiteButtonProps = {
  sellerWebsiteUrl?: string;
  handleWebsiteClick: () => void;
  isPrimary?: boolean;
};

const SellersContactWebsiteButton = ({
  sellerWebsiteUrl,
  handleWebsiteClick,
  isPrimary = false,
}: SellersContactWebsiteButtonProps) => {
  return (
    <MaskedLink
      target="_blank"
      rel="nofollow"
      href={sellerWebsiteUrl as string}
      className={`hz-Button ${isPrimary ? 'hz-Button--primary' : 'hz-Button--secondary'} ${Classes.button} ${Classes.link}`}
      onClick={handleWebsiteClick}
    >
      <ButtonIcon name="website" isStartIcon={false} />
      <Trans tagName="span" i18nKey="sellerContact.website" />
    </MaskedLink>
  );
};

const SellerContactOptions = ({
  itemId,
  title,
  adImageThumbnail,
  priceInfo,
  seller,
  isAutomotiveAd,
  isAdmarkt,
  hasCallTracking,
  allowTradeInRequest,
  traits,
  lowBidDetection,
  profile,
  isCarAd,
}: TSellerContactOptionsProps) => {
  const {
    name: sellerName,
    phoneNumber,
    isAsqEnabled,
    sellerWebsiteUrl,
    phoneNumberHidden,
    showProfilePicture,
    showSalesRepresentatives,
    financeAvailable,
    id: sellerId,
    sellerType,
    contactOptions,
  } = seller;

  const {
    xsrfToken,
    userLoggedIn,
    userDetails: { isAdOwner = false } = {},
    hasLicensePlate,
    links: { buyItNowPathPrefix },
    isMobileDevice,
    experiments: { isStickyCTAsEnabled },
  } = useContext(EnvironmentContext);

  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [isPhoneDialogLoaded, setPhoneDialogLoaded] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginSuccessAction, setLoginSuccessAction] = useState('');
  const [showPaymentProposalDialog, setShowPaymentProposalDialog] = useState(false);
  const [isPrimaryButtonSticky, setIsPrimaryButtonSticky] = useState(false);
  const primaryButtonRef = useRef<HTMLDivElement | null>(null);

  const reloadAndShowPhoneDialog = () => {
    appendHashParam(HASH_PARAMS.SHOW_PHONE.KEY, HASH_PARAMS.SHOW_PHONE.VALUE);
    document.location.reload();
  };

  const loadPhoneDialog = async (gaEvent?: GaEventProps) => {
    if (!isPhoneDialogLoaded) {
      try {
        PhoneDialog = (await import(/* webpackChunkName: "PhoneDialog" */ './PhoneDialog')).default;
        setPhoneDialogLoaded(true);
        if (gaEvent?.label) {
          trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SPhoneBegin, `click-${gaEvent.label}`, {
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_SELLER_TYPE,
              payload: sellerType,
            }),
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
              payload: hasLicensePlate,
            }),
          });
        } else {
          trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SPhoneBegin, 'click', {
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_SELLER_TYPE,
              payload: sellerType,
            }),
            ...createCustomDimension({
              dimension: GA_PARAMETER.AD_HAS_LICENSE_PLATE,
              payload: hasLicensePlate,
            }),
          });
        }
        registerPhoneClick({ itemId, xsrfToken });
      } catch (err) {}
    }
  };

  useEffect(() => {
    if (getHashParamValue(HASH_PARAMS.SHOW_PHONE.KEY) === HASH_PARAMS.SHOW_PHONE.VALUE) {
      deleteHashParam(HASH_PARAMS.SHOW_PHONE.KEY);
      if (!phoneNumberHidden) {
        loadPhoneDialog();
        setShowPhoneNumber(true);
      }
    }
    // keep deps empty to run useEffect hook only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobileDevice && (!isAutomotiveAd || isStickyCTAsEnabled)) {
      const elementRef = primaryButtonRef.current as Element;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsPrimaryButtonSticky(!entry.isIntersecting);
          });
        },
        {
          rootMargin: '-136px 0px 0px 0px',
          threshold: 0.5,
        },
      );

      if (primaryButtonRef.current) {
        observer.observe(elementRef);
      }

      return () => {
        if (elementRef) {
          observer.disconnect();
        }
      };
    }
  }, [isAutomotiveAd, isMobileDevice, isStickyCTAsEnabled]);

  const handleWebsiteClick = (gaEvent?: GaEventProps) => {
    trackGAEvent(
      GA_TRACK_EVENTS_ACTIONS.R2SExternalBegin,
      gaEvent ? gaEvent.label : '',
      createCustomDimension({
        dimension: GA_PARAMETER.AD_SELLER_TYPE,
        payload: sellerType,
      }),
    );
  };

  const buyItNowHandler = (gaEvent?: GaEventProps) => {
    if (gaEvent) {
      trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SBINBegin, gaEvent.label);
    } else {
      trackGAEvent(GA_TRACK_EVENTS_ACTIONS.R2SBINBegin);
    }
    location.assign(`${buyItNowPathPrefix}${itemId}`);
  };

  const loadAndShowPaymentDialog = async () => {
    PaymentProposalDialog = (
      await import(/* webpackChunkName: "PaymentProposalDialog" */ './PaymentProposal/PaymentProposalDialog')
    ).default;
    setShowPaymentProposalDialog(true);
  };

  const handleBuyItNowClick = (gaEvent?: GaEventProps) => {
    if (userLoggedIn) {
      buyItNowHandler(gaEvent);
    } else {
      setShowLoginDialog(true);
      setLoginSuccessAction('buyItNow');
    }
  };

  const handlePaymentProposalClick = () => {
    if (userLoggedIn) {
      loadAndShowPaymentDialog();
    } else {
      setShowLoginDialog(true);
      setLoginSuccessAction('paymentProposal');
    }
  };

  const handlePhoneNumberClick = (gaEvent?: GaEventProps) => {
    if (!phoneNumberHidden) {
      loadPhoneDialog(gaEvent);
      setShowPhoneNumber(true);
    } else {
      setShowLoginDialog(true);
      setLoginSuccessAction('phone');
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginDialog(false);
    switch (loginSuccessAction) {
      case 'phone':
        reloadAndShowPhoneDialog();
        break;

      case 'buyItNow':
        buyItNowHandler();
        break;

      case 'paymentProposal':
        loadAndShowPaymentDialog();
        break;
    }
  };

  const handlePhoneDialogClose = () => {
    setShowPhoneNumber(false);
  };

  // do not render an empty block if nothing to show
  if ((!isAsqEnabled || (isAsqEnabled && isAdOwner)) && !sellerWebsiteUrl && !phoneNumber && !showLoginDialog) {
    return null;
  }

  return (
    <>
      <div
        className={`${Classes.root} ${isPrimaryButtonSticky ? Classes.buttonPlaceholder : ''}`}
        ref={primaryButtonRef}
      >
        {contactOptions.map((type, index) => {
          const isPrimary = index === 0;
          return (
            <div key={type} {...(isPrimary && isPrimaryButtonSticky ? { className: Classes.sticky } : {})}>
              {type === CONTACT_OPTION_TYPES.BUY_IT_NOW && (
                <BuyItNowButton
                  onClick={() => {
                    handleBuyItNowClick();
                  }}
                />
              )}
              {type === CONTACT_OPTION_TYPES.ASQ && (
                <Asq
                  itemId={itemId}
                  allowTradeInRequest={allowTradeInRequest}
                  isAutomotiveAd={isAutomotiveAd}
                  title={title}
                  sellerName={sellerName}
                  sellerLocation={seller.location}
                  isPrimary={isPrimary}
                  sellerId={sellerId}
                  adImageThumbnail={adImageThumbnail}
                  showProfilePicture={showProfilePicture}
                  showSalesRepresentatives={showSalesRepresentatives}
                  financeAvailable={financeAvailable}
                  sellerType={sellerType}
                  priceInfo={priceInfo}
                  traits={traits}
                  lowBidDetection={lowBidDetection}
                  profile={profile}
                  isAdmarkt={isAdmarkt}
                  isCarAd={isCarAd}
                />
              )}
              {type === CONTACT_OPTION_TYPES.PHONE && (
                <>
                  {Boolean(profile.availableForCallingUntil && seller.withCallAvailabilityIndicator) && (
                    <div className={Classes.callAvailabilityIndicator}>
                      <Trans
                        className={Classes.highlight}
                        i18nKey={'sellerContact.callAvailability.available'}
                        tagName="span"
                      />
                      <Trans i18nKey={'sellerContact.callAvailability.until'} tagName="span" />
                      {profile.availableForCallingUntil}
                    </div>
                  )}
                  <SellerContactOptionsButton
                    isPrimary={isPrimary}
                    className={Classes.button}
                    onClick={handlePhoneNumberClick}
                  >
                    <ButtonIcon name="call" isStartIcon={false} />
                    <Trans tagName="span" i18nKey="sellerContact.showPhoneNumber" />
                  </SellerContactOptionsButton>
                </>
              )}
              {type === CONTACT_OPTION_TYPES.WEBSITE && (
                <SellersContactWebsiteButton
                  sellerWebsiteUrl={sellerWebsiteUrl}
                  handleWebsiteClick={() => handleWebsiteClick()}
                  isPrimary={isPrimary}
                />
              )}
              {type === CONTACT_OPTION_TYPES.PAYMENT_PROPOSAL && (
                <PaymentProposalButton isPrimary={isPrimary} onClick={handlePaymentProposalClick} />
              )}
            </div>
          );
        })}

        {isPhoneDialogLoaded && showPhoneNumber && (
          <PhoneDialog
            seller={seller}
            itemId={itemId}
            hasCallTracking={hasCallTracking}
            onClose={handlePhoneDialogClose}
          />
        )}
        {showLoginDialog && <LoginDialogContainer onClose={setShowLoginDialog} onSuccess={handleLoginSuccess} />}
        {showPaymentProposalDialog && (
          <PaymentProposalDialog itemId={itemId} hideModal={() => setShowPaymentProposalDialog(false)} />
        )}
      </div>
    </>
  );
};

export default SellerContactOptions;
